import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import {
  DropdownIndicator,
  IndicatorSeparator,
  formatGroupLabel,
  getTranslatedText,
  useProduct
} from '@nile/components';
import { GET_USER_ROLE_TYPES } from '@nile/banyan/src/Graphql/Mutations/role';
import { AccessControl, COMMON_CONSTANTS } from '@nile/components';
import {
  Button,
  ButtonGroup,
  ResourceItem,
  ResourceList
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, noop, random } from 'lodash';
import nanoid from 'nanoid';
import { useIntl } from 'react-intl';
import { AccountClient, GET_CLIENTS, useCachedQuery } from '@nile/graphql';
import { GET_COMPANY_SITE_LIST } from '@nile/itsm/src/graphql';

function RequesterShareFilter({
  data = [],
  updateData = noop,
  mappingKey,
  disabled = false,
  hideSite = false,
  hideAllClients = false
}) {
  const productType = useProduct();
  const intl = useIntl();
  const isITProduct = productType === COMMON_CONSTANTS.IT;
  const [selectedList, setSelectedList] = useState(data);
  const [shareableRoles, setRoles] = useState([]);
  const [clients, setClients] = useState([]);
  const ALL_SITES_SHARED_TYPE = 'AllSites';
  const ALL_SITES_LABEL = getTranslatedText(
    intl,
    'provisionRule.name.allSites'
  );
  const ALL_CLIENTS_SHARED_TYPE = isITProduct
    ? ALL_SITES_SHARED_TYPE
    : 'AllClients';
  const ALL_CLIENTS_LABEL = isITProduct
    ? ALL_SITES_LABEL
    : getTranslatedText(intl, 'common.all.clients');
  const ALL_ROLES_SHARED_TYPE = 'AllRoles';
  const ALL_ROLES_LABEL = getTranslatedText(intl, 'common.all.roles');
  const SITE_SHARED_TYPE = 'Site';
  const CLIENT_SHARED_TYPE = isITProduct ? SITE_SHARED_TYPE : 'Client';
  const ROLE_SHARED_TYPE = 'Role';
  const selectedClients = data?.map((a) =>
    isITProduct ? a?.site?.id : a?.client?.accountId
  );

  const { data: shareClientData, loading: shareClientLoading } = useCachedQuery(
    GET_CLIENTS,
    {
      client: AccountClient,
      variables: {
        listInfo: {
          sort: [
            {
              attribute: 'name',
              order: 'ASC'
            }
          ]
        }
      },
      skip: isITProduct || disabled
    }
  );
  const { data: shareSiteData, loading: shareSiteLoading } = useCachedQuery(
    GET_COMPANY_SITE_LIST,
    {
      client: AccountClient,
      variables: {
        input: {
          sort: [{ attribute: 'name', order: 'ASC' }]
        }
      },
      skip: !isITProduct || disabled || hideSite
    }
  );
  const { data: shareRolesData, loading: shareRolesLoading } = useCachedQuery(
    GET_USER_ROLE_TYPES,
    {
      client: AccountClient,
      skip: disabled
    }
  );
  const addNewShareDoc = () => {
    const baseObj =
      productType === COMMON_CONSTANTS.IT
        ? {
            site: {
              id: ''
            },
            roles: [],
            mappingId: random(data?.length, 15000)
          }
        : {
            client: {
              name: '',
              accountId: ''
            },
            roles: [],
            mappingId: random(data?.length, 15000)
          };

    setSelectedList((prev) => [...prev, baseObj]);
  };

  const removeMapping = (mappingIndex) => {
    const updatedAssetData = cloneDeep(selectedList);
    const currentIndex = mappingIndex;
    updatedAssetData.splice(currentIndex, 1);
    setSelectedList(() => updatedAssetData);
    if (updateData) updateData(updatedAssetData, mappingKey, true);
    return updatedAssetData;
  };

  const handleClientChange = (newValue, index) => {
    const updatedAssetData = cloneDeep(selectedList);
    const currentIndex = index;
    if (newValue.value === ALL_CLIENTS_SHARED_TYPE) {
      updatedAssetData[currentIndex].client = null;
      updatedAssetData[currentIndex].clientSharedType = ALL_CLIENTS_SHARED_TYPE;
      updatedAssetData[currentIndex].disableAllRoles = true;
    } else {
      updatedAssetData[currentIndex].client = {
        accountId: newValue.value,
        name: newValue.name
      };
      updatedAssetData[currentIndex].clientSharedType = CLIENT_SHARED_TYPE;
    }
    updatedAssetData[currentIndex].sharedType = 'SPECIFIC';
    updatedAssetData[currentIndex].portalType = 'REQUESTER';
    setSelectedList(updatedAssetData);
    if (updateData) updateData(updatedAssetData, mappingKey, true);
  };
  const handleSiteChange = (newValue, index) => {
    const updatedAssetData = cloneDeep(selectedList);
    const currentIndex = index;
    if (newValue.value === ALL_CLIENTS_SHARED_TYPE) {
      updatedAssetData[currentIndex].site = null;
      updatedAssetData[currentIndex].siteSharedType = ALL_CLIENTS_SHARED_TYPE;
      updatedAssetData[currentIndex].disableAllRoles = true;
    } else {
      updatedAssetData[currentIndex].site = {
        id: newValue.value,
        name: newValue.name
      };
      updatedAssetData[currentIndex].siteSharedType = CLIENT_SHARED_TYPE;
    }
    updatedAssetData[currentIndex].sharedType = 'SPECIFIC';
    updatedAssetData[currentIndex].portalType = 'REQUESTER';
    setSelectedList(updatedAssetData);
    if (updateData) updateData(updatedAssetData, mappingKey, true);
  };
  const handleRoleChange = (newValue, index) => {
    const updatedAssetData = cloneDeep(selectedList);
    const currentIndex = index;
    if (newValue?.find((r) => r.value === ALL_ROLES_SHARED_TYPE)) {
      updatedAssetData[currentIndex].roles = null;
      updatedAssetData[currentIndex].userRoleSharedType = ALL_ROLES_SHARED_TYPE;
      updatedAssetData[currentIndex].disableAllClientSite = true;
    } else {
      updatedAssetData[currentIndex].roles =
        newValue?.map((r) => {
          return {
            roleId: r.value,
            name: r.name
          };
        }) || null;
      updatedAssetData[currentIndex].userRoleSharedType = ROLE_SHARED_TYPE;
    }
    updatedAssetData[currentIndex].sharedType = 'SPECIFIC';
    updatedAssetData[currentIndex].portalType = 'REQUESTER';
    setSelectedList(updatedAssetData);
    if (updateData) updateData(updatedAssetData, mappingKey, true);
  };

  const getDropdownFormat = (name, value) => {
    if (!name) {
      return null;
    }
    return {
      name,
      value,
      label: name
    };
  };

  const getSelectedClient = (account, sharedType, site) => {
    if (sharedType === ALL_CLIENTS_SHARED_TYPE) {
      return getDropdownFormat(ALL_CLIENTS_LABEL, ALL_CLIENTS_SHARED_TYPE);
    }
    if (isITProduct) {
      return getDropdownFormat(site?.name, site?.id);
    }
    return getDropdownFormat(account?.name, account?.accountId);
  };

  const getSelectedRole = (role, sharedType) => {
    if (sharedType === ALL_ROLES_SHARED_TYPE) {
      return getDropdownFormat(ALL_ROLES_LABEL, ALL_ROLES_SHARED_TYPE);
    }
    return role?.map((r) => {
      return getDropdownFormat(r.name, r.roleId);
    });
  };

  const getUniqueRoles = () => {
    if (!isITProduct) return shareableRoles;
    const selectedRoles = selectedList?.map((el) => el?.roles)?.flat();
    if (selectedRoles?.length) {
      const uniqueRoles = shareableRoles.filter((r) => {
        return !selectedRoles.some((s) => s?.roleId === r?.roleId);
      });
      return uniqueRoles;
    }
    return shareableRoles;
  };

  const getUniqueClients = (selectedClientList, account, site) => {
    if (selectedClientList.length) {
      if (isITProduct) {
        const uniqueSites = clients.filter((s) => {
          if (site?.id === s.value) {
            return true;
          }
          return !selectedClientList.includes(s.value);
        });
        return uniqueSites;
      }
      const uniqueClients = clients.filter((s) => {
        if (account?.accountId === s.value) {
          return true;
        }
        return !selectedClientList.includes(s.value);
      });
      return uniqueClients;
    }
    return clients;
  };

  const getAllClients = (clientsData) => {
    if (!clientsData) return [];
    const allClients = clientsData.map((c) => {
      return {
        label: c.name,
        value: isITProduct ? c.id : c.accountId,
        name: c.name,
        accountId: isITProduct ? c.id : c.accountId,
        sharedType: isITProduct ? SITE_SHARED_TYPE : CLIENT_SHARED_TYPE
      };
    });
    if (!isITProduct && !hideAllClients) {
      // only when the IT document is not assiociated to any client, display 'All Clients'
      allClients.unshift({
        label: ALL_CLIENTS_LABEL,
        value: ALL_CLIENTS_SHARED_TYPE,
        name: ALL_CLIENTS_LABEL,
        accountId: null,
        sharedType: ALL_CLIENTS_SHARED_TYPE,
        valueType: 'ALL'
      });
    }
    if (isITProduct) {
      // only when the IT document is not assiociated to any site, display 'All Site'
      allClients.unshift({
        label: ALL_SITES_LABEL,
        value: ALL_SITES_SHARED_TYPE,
        name: ALL_SITES_LABEL,
        id: null,
        sharedType: ALL_SITES_SHARED_TYPE,
        valueType: 'ALL'
      });
    }
    return allClients;
  };

  const getAllRoles = (rolesData = []) => {
    const allRoles = rolesData.map((r) => {
      return {
        label: r?.name,
        value: r?.roleId,
        name: r?.name,
        roleId: r?.roleId,
        sharedType: ROLE_SHARED_TYPE
      };
    });
    if (!isITProduct) {
      allRoles.unshift({
        label: ALL_ROLES_LABEL,
        value: ALL_ROLES_SHARED_TYPE,
        name: ALL_ROLES_LABEL,
        sharedType: ALL_ROLES_SHARED_TYPE,
        roleId: null,
        valueType: 'ALL'
      });
    }
    return allRoles;
  };

  useEffect(() => {
    if (shareClientData) {
      const generateClientDropdown = getAllClients(
        shareClientData?.getClientList?.clients
      );
      setClients(generateClientDropdown);
    }
  }, [shareClientData]);

  useEffect(() => {
    if (shareSiteData) {
      const generateSiteDropdown = getAllClients(
        shareSiteData?.getCompanySiteList?.sites
      );
      setClients(generateSiteDropdown);
    }
  }, [shareSiteData]);

  useEffect(() => {
    if (shareRolesData) {
      const generateRolesDropdown = getAllRoles(
        shareRolesData?.getUserRoleTypeRoles
      );
      setRoles(generateRolesDropdown);
    }
  }, [shareRolesData]);

  useEffect(() => {
    const initObject =
      productType === COMMON_CONSTANTS.IT
        ? {
            site: {
              id: ''
            },
            roles: [],
            mappingId: random(data?.length, 15000)
          }
        : {
            client: {
              name: '',
              accountId: ''
            },
            roles: [],
            mappingId: random(data?.length, 15000)
          };
    if (disabled) {
      setSelectedList([initObject]);
      setClients([]);
      setRoles([]);
    } else {
      const defaultShareList = [initObject];
      setSelectedList(data?.length ? data : defaultShareList);
    }
  }, [data?.length, disabled]);

  return (
    <>
      <ResourceList
        items={selectedList}
        renderItem={(item, index) => {
          const {
            client,
            roles,
            clientSharedType,
            siteSharedType,
            userRoleSharedType,
            site,
            disableAllClientSite = false,
            disableAllRoles = false
          } = item;
          const isAnyAllClientSelected = selectedList.find((ac) =>
            isITProduct
              ? ac.siteSharedType === ALL_CLIENTS_SHARED_TYPE
              : ac.clientSharedType === ALL_CLIENTS_SHARED_TYPE
          );
          const sharedType = isITProduct ? siteSharedType : clientSharedType;
          const selectedClient = getSelectedClient(client, sharedType, site);
          const selectedRole = getSelectedRole(roles, userRoleSharedType);
          let uniqueClients = getUniqueClients(selectedClients, client, site);
          if (
            disableAllClientSite ||
            (selectedList.length > 1 && !isAnyAllClientSelected)
          ) {
            uniqueClients = uniqueClients.filter((s) => s.valueType !== 'ALL');
          }
          let uniqueRoles = getUniqueRoles();
          if (disableAllRoles) {
            uniqueRoles = uniqueRoles.filter((s) => s.valueType !== 'ALL');
          }

          return (
            <ResourceItem
              id={nanoid()}
              gutter="xs"
              key={nanoid()}
              hideSelectedBgColor
            >
              <div className="flex fx-sb">
                <AccessControl
                  productToCheck={COMMON_CONSTANTS.MSP}
                  render={
                    <div className="w250">
                      <div className="flex-row flex-align-center item-select-container">
                        <div className="w-35 flex actr jctr">
                          <SVGIcon name="ClientLogo" />
                        </div>
                        <Select
                          className="select-block w100 ml-10"
                          onChange={(val) => {
                            handleClientChange(val, index);
                          }}
                          components={{
                            IndicatorSeparator,
                            DropdownIndicator
                          }}
                          menuPortalTarget={document.body}
                          classNamePrefix="value_selector"
                          placeholder={getTranslatedText(
                            intl,
                            'asset.sidebar.itDoc.chooseClient.pl'
                          )}
                          formatGroupLabel={formatGroupLabel}
                          menuPlacement="auto"
                          value={selectedClient}
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999
                            })
                          }}
                          isDisabled={disabled}
                          options={uniqueClients}
                          isLoading={shareClientLoading}
                        />
                      </div>
                    </div>
                  }
                />
                {!hideSite && (
                  <AccessControl
                    productToCheck={COMMON_CONSTANTS.IT}
                    render={
                      <div className="w250">
                        <div className="flex-row flex-align-center item-select-container">
                          <div className="w-35 flex actr jctr">
                            <SVGIcon name="Site" />
                          </div>
                          <div className="w100">
                            <Select
                              className="select-block w100 ml-10"
                              onChange={(val) => {
                                handleSiteChange(val, index);
                              }}
                              components={{
                                IndicatorSeparator,
                                DropdownIndicator
                              }}
                              menuPortalTarget={document.body}
                              classNamePrefix="value_selector"
                              placeholder={getTranslatedText(
                                intl,
                                'asset.sidebar.itDoc.chooseSite.pl'
                              )}
                              formatGroupLabel={formatGroupLabel}
                              menuPlacement="auto"
                              value={selectedClient}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999
                                })
                              }}
                              isDisabled={disabled}
                              options={uniqueClients}
                              isLoading={shareSiteLoading}
                            />
                          </div>
                        </div>
                      </div>
                    }
                  />
                )}
                <div className="w250">
                  <div className="w100">
                    <div className="flex-row item-select-container">
                      <div className="w-35 flex actr jctr">
                        <SVGIcon name="ProjectLogo" />
                      </div>
                      <Select
                        className="select-block w100 ml-10"
                        onChange={(val) => {
                          handleRoleChange(val, index);
                        }}
                        components={{
                          IndicatorSeparator,
                          DropdownIndicator
                        }}
                        menuPortalTarget={document.body}
                        classNamePrefix="value_selector"
                        placeholder={getTranslatedText(
                          intl,
                          'client.details.user.chooseRole'
                        )}
                        isOptionDisabled={() =>
                          userRoleSharedType === ALL_ROLES_SHARED_TYPE
                        }
                        formatGroupLabel={formatGroupLabel}
                        menuPlacement="auto"
                        value={selectedRole}
                        styles={{
                          menuPortal: (base) => {
                            return {
                              ...base,
                              zIndex: 9999
                            };
                          }
                        }}
                        options={uniqueRoles}
                        isMulti
                        isDisabled={disabled}
                        isLoading={shareRolesLoading}
                      />
                    </div>
                  </div>
                </div>
                <>
                  {!(disabled || isAnyAllClientSelected) && (
                    <ButtonGroup>
                      {selectedList?.length > 1 && (
                        <Button
                          plainSecondary
                          icon={
                            <SVGIcon
                              name="CircleMinus"
                              color="red !important"
                            />
                          }
                          onClick={() => {
                            removeMapping(index);
                          }}
                        />
                      )}

                      <Button
                        plainSecondary
                        icon={
                          <SVGIcon name="CirclePlus" color="green !important" />
                        }
                        onClick={() => {
                          addNewShareDoc();
                        }}
                      />
                    </ButtonGroup>
                  )}
                </>
              </div>
            </ResourceItem>
          );
        }}
      />
    </>
  );
}

export default RequesterShareFilter;
